import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { NumberParam, useQueryParam, withDefault } from 'use-query-params';
import { useShopTime } from '@modules/Timezone/providers/ShopTimeProvider';
import { useCurrentShop } from '../../../../../../hooks/useCurrentShop';
import { DEFAULT_DATE_RANGE_INDEX, dateRangeOptions } from './consts';
// Helper function to check if dates match with day precision
const areDatesEqual = (date1, date2) => {
    return date1.toDateString() === date2.toDateString();
};
// Helper function to validate date
const isValidDate = (date) => {
    return !isNaN(date.getTime());
};
export const useDateRangeParams = () => {
    const { currentShop } = useCurrentShop({ staleTime: Infinity });
    const { convertDateToTimestamp, convertTimestampToDate } = useShopTime();
    const [rangeFromParam, setRangeFromParam] = useQueryParam('rangeFrom', withDefault(NumberParam, undefined));
    const [rangeToParam, setRangeToParam] = useQueryParam('rangeTo', withDefault(NumberParam, undefined));
    const options = useMemo(() => {
        if (!currentShop?.createdAt)
            return dateRangeOptions;
        const allTimeOption = {
            label: 'All time',
            value: 'allTime',
            dates: [dayjs(currentShop.createdAt).startOf('day').toDate(), dayjs().endOf('day').toDate()],
        };
        return [...dateRangeOptions, allTimeOption];
    }, [currentShop]);
    const dateRange = useMemo(() => {
        // If no date parameters, set default option
        if (rangeFromParam === undefined || rangeToParam === undefined) {
            return dateRangeOptions[DEFAULT_DATE_RANGE_INDEX];
        }
        // Convert timestamp numbers to Date objects
        const fromDate = convertTimestampToDate(rangeFromParam);
        const toDate = convertTimestampToDate(rangeToParam);
        // Check if dates are valid
        if (!isValidDate(fromDate) || !isValidDate(toDate)) {
            return dateRangeOptions[DEFAULT_DATE_RANGE_INDEX]; // fallback to default
        }
        // Check if dates match any predefined option
        const matchingOption = dateRangeOptions.find((option) => {
            if (option.dates.length !== 2)
                return false;
            const [optionFrom, optionTo] = option.dates;
            // Compare dates with day precision
            return areDatesEqual(optionFrom, fromDate) && areDatesEqual(optionTo, toDate);
        });
        if (matchingOption) {
            // If it matches a predefined option, return it
            return matchingOption;
        }
        // If no match, return custom DateRangeValue with dates only
        return {
            dates: [fromDate, toDate],
        };
    }, [rangeFromParam, rangeToParam]);
    const setDateRange = useCallback((newDateRange) => {
        if (newDateRange.dates.length >= 2) {
            const [fromDate, toDate] = newDateRange.dates;
            setRangeFromParam(convertDateToTimestamp(fromDate));
            setRangeToParam(convertDateToTimestamp(toDate));
        }
    }, [convertDateToTimestamp, setRangeFromParam, setRangeToParam]);
    return {
        options,
        dateRange,
        setDateRange,
    };
};
